import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Snackbar,
  styled
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Tv as TvIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { mainApi } from '../../config/apiClient';
import {
  getPersonnel as getPersonnelSql,
  getScreenPermissionsByType,
  bulkSetScreenPermissions,
  deleteScreenPermission,
} from '../../services/migration/personnelDataService';
import GuideButton from '../common/guide/GuideButton';
const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

interface QRMenu {
  id: string;
  name: string;
  branchName?: string;
}

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface PoleDisplayPermissions {
  accessPoleDisplay: boolean;
  autoRedirect: boolean;
}

const PoleDisplayManagement: React.FC = () => {
  const { userDetails } = useAuth();
  const navigate = useNavigate();
  
  const [menus, setMenus] = useState<QRMenu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<QRMenu | null>(null);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState<Map<string, PoleDisplayPermissions>>(new Map());
  const [saving, setSaving] = useState(false);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Menüleri yükle
  useEffect(() => {
    const fetchMenus = async () => {
      if (!userDetails?.companyId) return;

      setLoading(true);
      setError(null);

      try {
        const timestamp = new Date().getTime();
        const response = await mainApi.get(`/api/QRMenu/menu-definitions`, {
          params: { _t: timestamp },
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        
        const result = response.data;
        if (result.success && result.data) {
          const menuList: QRMenu[] = result.data.map((menu: any) => ({
            id: menu.id,
            name: menu.name || 'İsimsiz Menü',
            branchName: menu.branchName
          }));
          
          setMenus(menuList);
        }
      } catch (error) {
        console.error('❌ Menü yükleme hatası:', error);
        setError('Menüler yüklenirken hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchMenus();
  }, [userDetails?.companyId]);

  // Personel listesini yükle
  const fetchPersonnel = async () => {
    if (!userDetails?.companyId) return;

    try {
      // Faz 3: personel listesi SQL'den (personnelDataService).
      const sqlPersonnel = await getPersonnelSql(userDetails.companyId);
      const personnelList: Personnel[] = (sqlPersonnel || []).map((p: any) => ({
        id: p.id, // Firebase UID
        firstName: p.firstName || p.name || '',
        lastName: p.lastName || '',
        role: p.role || 'employee',
      }));
      console.log(`✅ SQL'den ${personnelList.length} personel yüklendi`);
      setPersonnel(personnelList);
    } catch (error) {
      console.error('❌ Personel yükleme hatası:', error);
    }
  };

  // Mevcut yetkileri yükle
  const loadExistingPermissions = async (menuId: string) => {
    if (!userDetails?.companyId) return;

    // poleDisplayScreens yetkileri SADECE SQL'den.
    const permissionsMap = new Map<string, PoleDisplayPermissions>();

    await Promise.all(personnel.map(async (person) => {
      let permissions: any = null;
      try {
        const rows = await getScreenPermissionsByType(person.id, 'poleDisplayScreens');
        const row = rows.find(r => String(r.screenId) === String(menuId) && r.isEnabled);
        if (row && row.settings) permissions = row.settings;
      } catch (e) {
        console.warn('[PoleDisplayManagement] SQL yetki okuma hatasi:', e);
      }
      if (permissions) permissionsMap.set(person.id, permissions);
    }));

    setSelectedPersonnel(permissionsMap);
  };

  // Yetki modalını aç
  const handleOpenPermissionModal = async (menu: QRMenu) => {
    setSelectedMenu(menu);
    setPermissionModalOpen(true);
    
    if (personnel.length === 0) {
      await fetchPersonnel();
    }
    
    await loadExistingPermissions(menu.id);
  };

  // Personel seçimini toggle et
  const handleTogglePersonnel = (personnelId: string, checked: boolean, field: 'accessPoleDisplay' | 'autoRedirect') => {
    setSelectedPersonnel(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(personnelId) || { accessPoleDisplay: false, autoRedirect: false };
      
      newMap.set(personnelId, {
        ...current,
        [field]: checked
      });
      
      return newMap;
    });
  };

  // Yetkileri kaydet
  const handleSavePermissions = async () => {
    if (!selectedMenu || !userDetails?.companyId) return;

    setSaving(true);

    try {
      // Faz 3: poleDisplayScreens yetkilerini SQL'e yaz (yetkili=upsert, yetkisiz=sil).
      const toSet: Array<{ firebaseUid: string; screenType: string; screenId: string; isEnabled: boolean; settings: any }> = [];
      const toDelete: string[] = [];
      for (const person of personnel) {
        const permissions = selectedPersonnel.get(person.id);
        if (permissions && (permissions.accessPoleDisplay || permissions.autoRedirect)) {
          toSet.push({
            firebaseUid: person.id,
            screenType: 'poleDisplayScreens',
            screenId: String(selectedMenu.id),
            isEnabled: true,
            settings: permissions,
          });
        } else {
          toDelete.push(person.id);
        }
      }
      if (toSet.length > 0) await bulkSetScreenPermissions(toSet);
      await Promise.all(toDelete.map((uid) => deleteScreenPermission(uid, 'poleDisplayScreens', String(selectedMenu.id)).catch(() => 0)));

      setSnackbarMessage('Yetkiler başarıyla kaydedildi!');
      setSnackbarOpen(true);
      setPermissionModalOpen(false);
    } catch (error) {
      console.error('❌ Yetki kaydetme hatası:', error);
      setSnackbarMessage('Yetkiler kaydedilirken hata oluştu!');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  // Müşteri ekranını aç
  const handleOpenPoleDisplay = (menuId: string) => {
    window.open(`#/musteri-ekrani/${menuId}`, '_blank');
  };

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TvIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            Müşteri Ekranı Yönetimi
          </Typography>
          <GuideButton guideKey="sales.poleDisplay" />
        </Box>
      </HeaderContainer>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {menus.length === 0 && !error ? (
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <TvIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Menü Bulunamadı
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Müşteri ekranı için QR menü tanımlanmamış.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    <TvIcon />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Menü Adı
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Şube
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
                    İşlemler
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {menus.map((menu) => (
                  <TableRow key={menu.id} hover>
                    <TableCell>
                      <TvIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6" fontWeight="bold">
                        {menu.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" color="text.secondary">
                        {menu.branchName || 'Şube Atanmamış'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenPermissionModal(menu)}
                          title="Personel Yetkilerini Ayarla"
                        >
                          <SettingsIcon />
                        </IconButton>
                        <IconButton
                          color="success"
                          onClick={() => handleOpenPoleDisplay(menu.id)}
                          title="Müşteri Ekranını Aç"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Personel Yetkilendirme Modalı - BASİT VERSİYON */}
      <Dialog
        open={permissionModalOpen}
        onClose={() => !saving && setPermissionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Personel Yetkileri - {selectedMenu?.name}
        </DialogTitle>
        
        <DialogContent dividers>
          {personnel.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Personel listesi yükleniyor...
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Personel</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Erişim</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Otomatik Aç</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {personnel.map((person) => {
                    const permissions = selectedPersonnel.get(person.id) || { accessPoleDisplay: false, autoRedirect: false };
                    const fullName = `${person.firstName} ${person.lastName}`.trim();
                    const displayName = fullName || person.firstName || person.lastName || 'İsimsiz';
                    
                    console.log('🔍 [MODAL DEBUG] Person render:', {
                      id: person.id,
                      firstName: person.firstName,
                      lastName: person.lastName,
                      fullName: fullName,
                      displayName: displayName
                    });
                    
                    return (
                      <TableRow key={person.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {person.role === 'employee' ? 'Personel' : person.role === 'courier' ? 'Kurye' : 'Yönetici'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={permissions.accessPoleDisplay}
                            onChange={(e) => handleTogglePersonnel(person.id, e.target.checked, 'accessPoleDisplay')}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={permissions.autoRedirect}
                            onChange={(e) => handleTogglePersonnel(person.id, e.target.checked, 'autoRedirect')}
                            disabled={!permissions.accessPoleDisplay}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setPermissionModalOpen(false)} disabled={saving}>
            İptal
          </Button>
          <Button onClick={handleSavePermissions} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar Bildirimi */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarMessage.includes('hata') ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default PoleDisplayManagement;

